<template>
  <div class="multiselect-container" ref="container">
    <label v-if="label" class="multiselect-label">{{ label }}</label>
    <div
      class="multiselect-trigger"
      :class="triggerClass"
      @click="toggle"
      tabindex="0"
      @keydown="handleKeydown"
      @blur="handleBlur"
      ref="trigger"
    >
      <div class="multiselect-content">
        <span class="multiselect-value" :class="{ 'multiselect-placeholder': modelValue.length === 0 }">
          {{ displayValue }}
        </span>
        <vue-feather
          :type="isOpen ? 'chevron-up' : 'chevron-down'"
          size="18"
          class="multiselect-icon"
        />
      </div>
    </div>
    <!-- Dropdown -->
    <FloatingDropdown ref="floatingDropdown" :open="isOpen" :trigger-ref="$refs.trigger" @dismiss="close">
      <div class="multiselect-dropdown-portal">
        <div class="multiselect-search-wrapper">
          <input
            ref="searchInput"
            v-model="searchQuery"
            class="multiselect-search-input"
            placeholder="Search..."
            autocomplete="nope"
            data-lpignore="true"
            data-form-type="other"
            @keydown.stop="handleSearchKeydown"
            @click.stop
          />
        </div>
        <div
          v-if="showSelectAllRow"
          class="multiselect-dropdown-option multiselect-dropdown-select-all"
          :class="{ 'multiselect-dropdown-option--focused': isOpen && focusedIndex === -1 }"
          @click.stop="toggleAllRows"
          @mouseenter="focusedIndex = -1"
        >
          <div class="multiselect-dropdown-checkbox" :class="{ 'multiselect-dropdown-checkbox--checked': anyRowSelected }">
            <vue-feather v-if="anyRowSelected" :type="allRowsSelected ? 'check' : 'minus'" size="14" />
          </div>
          <div class="multiselect-dropdown-option-content">
            <span class="multiselect-dropdown-option-label">{{ selectAllLabel }}</span>
          </div>
        </div>
        <div
          v-for="(option, index) in filteredDisplayOptions"
          :key="option.value"
          class="multiselect-dropdown-option"
          :class="{
            'multiselect-dropdown-option--selected': modelValue.includes(option.value),
            'multiselect-dropdown-option--focused': index === focusedIndex,
            'multiselect-dropdown-option--locked': option.locked,
            'multiselect-dropdown-option--removed': option._removed
          }"
          @click.stop="!option.locked && toggleOption(option)"
          @mouseenter="focusedIndex = index"
        >
          <div class="multiselect-dropdown-checkbox" :class="{ 'multiselect-dropdown-checkbox--checked': modelValue.includes(option.value) }">
            <vue-feather v-if="modelValue.includes(option.value)" type="check" size="14" />
          </div>
          <div class="multiselect-dropdown-option-content">
            <span class="multiselect-dropdown-option-label">{{ option.label }}</span>
            <span v-if="option.sublabel" class="multiselect-dropdown-option-sublabel">{{ option.sublabel }}</span>
          </div>
        </div>
        <div v-if="filteredDisplayOptions.length === 0" class="multiselect-no-results">
          No results found
        </div>
      </div>
    </FloatingDropdown>
    <span v-if="error" class="multiselect-error">{{ error }}</span>
  </div>
</template>

<script>
import VueFeather from 'vue-feather'
import { FloatingDropdown } from '@/design'

export default {
  name: 'MultiSelectDropdown',
  components: {
    'vue-feather': VueFeather,
    FloatingDropdown
  },
  props: {
    // Array of selected values
    modelValue: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Select...'
    },
    // Array of { value, label, sublabel? } objects
    options: {
      type: Array,
      required: true,
      validator: (options) => options.every(opt => 'value' in opt && 'label' in opt)
    },
    disabled: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    },
    // Custom display function for selected items
    // If not provided, shows count or comma-separated labels
    displayFormatter: {
      type: Function,
      default: null
    },
    // Adds a "Select all" row above the options that checks / unchecks every row the search shows
    showSelectAll: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOpen: false,
      // -1 while open = the "Select all" row (when shown)
      focusedIndex: -1,
      searchQuery: '',
      displayOptions: []
    }
  },
  computed: {
    triggerClass() {
      return {
        'multiselect-trigger--open': this.isOpen,
        'multiselect-trigger--disabled': this.disabled,
        'multiselect-trigger--error': this.error
      }
    },
    displayValue() {
      if (this.modelValue.length === 0) return this.placeholder

      if (this.displayFormatter) {
        return this.displayFormatter(this.modelValue, this.options)
      }

      // If only 1 selected, show its label
      if (this.modelValue.length === 1) {
        const selectedOption = this.options.find(opt => opt.value === this.modelValue[0])
        return selectedOption ? selectedOption.label : this.modelValue[0]
      }

      // 2 or more: show "X Selected" format
      return `${this.modelValue.length} Selected`
    },
    filteredDisplayOptions() {
      if (!this.searchQuery.trim()) return this.displayOptions
      const query = this.searchQuery.trim().toLowerCase()
      return this.displayOptions.filter(opt =>
        opt.label.toLowerCase().includes(query) ||
        (opt.sublabel && opt.sublabel.toLowerCase().includes(query))
      )
    },
    // The rows "Select all" acts on: everything the search shows except locked rows
    selectableRows() {
      return this.filteredDisplayOptions.filter(opt => !opt.locked)
    },
    showSelectAllRow() {
      return this.showSelectAll && this.selectableRows.length > 0
    },
    anyRowSelected() {
      return this.selectableRows.some(opt => this.modelValue.includes(opt.value))
    },
    allRowsSelected() {
      return this.selectableRows.length > 0 && this.selectableRows.every(opt => this.modelValue.includes(opt.value))
    },
    selectAllLabel() {
      return this.searchQuery.trim() ? 'Select all results' : 'Select all'
    }
  },
  watch: {
    searchQuery() {
      this.focusedIndex = 0
    }
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick)
  },
  unmounted() {
    document.removeEventListener('click', this.handleOutsideClick)
  },
  methods: {
    buildDisplayOptions() {
      // Removed items: values in modelValue that no longer exist in options
      const removedOpts = this.modelValue
        .filter(val => !this.options.some(opt => opt.value === val))
        .map(val => ({ value: val, label: val, _removed: true }))

      // Selected items that still exist
      const selectedOpts = this.options.filter(opt => this.modelValue.includes(opt.value))

      // Unselected items
      const unselectedOpts = this.options.filter(opt => !this.modelValue.includes(opt.value))

      // Order: removed (pinned top) → selected (pinned) → unselected
      this.displayOptions = [...removedOpts, ...selectedOpts, ...unselectedOpts]
    },
    toggle() {
      if (this.disabled) return
      this.isOpen = !this.isOpen
      if (this.isOpen) {
        this.searchQuery = ''
        this.buildDisplayOptions()
        this.focusedIndex = 0
        this.$nextTick(() => {
          this.$refs.searchInput?.focus()
        })
      }
    },
    close() {
      this.isOpen = false
      this.focusedIndex = -1
      this.searchQuery = ''
    },
    toggleOption(option) {
      const newValue = [...this.modelValue]
      const index = newValue.indexOf(option.value)

      if (index === -1) {
        newValue.push(option.value)
      } else {
        newValue.splice(index, 1)
      }

      this.$emit('update:modelValue', newValue)
      this.$emit('change', newValue)
      // Keep dropdown open for multi-select
    },
    toggleAllRows() {
      const rowValues = this.selectableRows.map(opt => opt.value)
      const newValue = this.allRowsSelected
        ? this.modelValue.filter(val => !rowValues.includes(val))
        : [...this.modelValue, ...rowValues.filter(val => !this.modelValue.includes(val))]

      this.$emit('update:modelValue', newValue)
      this.$emit('change', newValue)
    },
    handleOutsideClick(event) {
      if (!this.isOpen) return
      const container = this.$refs.container
      if (container && !container.contains(event.target)) {
        this.close()
      }
    },
    handleBlur() {
      // Only close if focus moves outside the container and the teleported dropdown
      setTimeout(() => {
        const container = this.$refs.container
        const active = document.activeElement
        if (container && !container.contains(active) && !this.$refs.floatingDropdown?.containsElement(active)) {
          this.close()
        }
      }, 100)
    },
    handleKeydown(event) {
      if (this.disabled) return

      switch (event.key) {
        case 'Enter':
        case ' ':
          event.preventDefault()
          if (this.isOpen && this.focusedIndex === -1 && this.showSelectAllRow) {
            this.toggleAllRows()
          } else if (this.isOpen && this.focusedIndex >= 0 && this.focusedIndex < this.filteredDisplayOptions.length) {
            const opt = this.filteredDisplayOptions[this.focusedIndex]
            if (!opt.locked) this.toggleOption(opt)
          } else {
            this.toggle()
          }
          break
        case 'Escape':
          event.preventDefault()
          this.close()
          break
        case 'ArrowDown':
          event.preventDefault()
          if (!this.isOpen) {
            this.toggle()
          } else {
            this.focusedIndex = Math.min(this.focusedIndex + 1, this.filteredDisplayOptions.length - 1)
          }
          break
        case 'ArrowUp':
          event.preventDefault()
          if (this.isOpen) {
            this.focusedIndex = Math.max(this.focusedIndex - 1, this.showSelectAllRow ? -1 : 0)
          }
          break
        case 'Tab':
          this.close()
          break
      }
    },
    handleSearchKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          this.focusedIndex = Math.min(this.focusedIndex + 1, this.filteredDisplayOptions.length - 1)
          break
        case 'ArrowUp':
          event.preventDefault()
          this.focusedIndex = Math.max(this.focusedIndex - 1, this.showSelectAllRow ? -1 : 0)
          break
        case 'Enter':
          event.preventDefault()
          if (this.focusedIndex === -1 && this.showSelectAllRow) {
            this.toggleAllRows()
          } else if (this.focusedIndex >= 0 && this.focusedIndex < this.filteredDisplayOptions.length) {
            const opt = this.filteredDisplayOptions[this.focusedIndex]
            if (!opt.locked) this.toggleOption(opt)
          }
          break
        case 'Escape':
          event.preventDefault()
          this.close()
          this.$refs.trigger?.focus()
          break
      }
    },
    focus() {
      this.$refs.trigger.focus()
    }
  },
  emits: ['update:modelValue', 'change']
}
</script>

<style scoped>
.multiselect-container {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.multiselect-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.multiselect-trigger {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 45px;
  padding: 0 14px;
  background: #FFF;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  box-sizing: border-box;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
  user-select: none;
}

.multiselect-trigger:hover:not(.multiselect-trigger--disabled):not(.multiselect-trigger--error) {
  border-color: #9ca3af;
}

.multiselect-trigger:focus:not(.multiselect-trigger--error),
.multiselect-trigger--open:not(.multiselect-trigger--error) {
  border-color: var(--color-primary, #6a16f0);
  box-shadow: 0 0 0 3px rgba(106, 22, 240, 0.1);
}

.multiselect-trigger--disabled {
  background: #f3f4f6;
  opacity: 0.6;
  cursor: not-allowed;
}

.multiselect-trigger--error {
  border-color: #dc2626;
}

.multiselect-trigger--error:focus,
.multiselect-trigger--error.multiselect-trigger--open {
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
}

.multiselect-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
}

.multiselect-value {
  font-size: 1rem;
  font-weight: 400;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.multiselect-placeholder {
  color: #9ca3af;
}

.multiselect-icon {
  color: #9ca3af;
  flex-shrink: 0;
  margin-left: 8px;
  transition: color 0.15s ease;
}

.multiselect-trigger:focus .multiselect-icon,
.multiselect-trigger--open .multiselect-icon {
  color: var(--color-primary, #6a16f0);
}

.multiselect-error {
  font-size: 0.8125rem;
  color: #dc2626;
}
</style>

<style>
/* Teleported Dropdown Menu - not scoped so it applies when teleported to body */
.multiselect-dropdown-portal {
  background: #FFF;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.05);
  border-radius: 6px;
  z-index: 10000;
  max-height: min(var(--fd-max-height, 9999px), 320px);
  overflow-y: auto;
  padding: 4px;
}

.multiselect-search-wrapper {
  padding: 4px 4px 0;
}

.multiselect-search-input {
  width: 100%;
  padding: 8px 10px;
  font-size: 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  outline: none;
  box-sizing: border-box;
  margin-bottom: 4px;
}

.multiselect-search-input:focus {
  border-color: var(--color-primary, #6a16f0);
}

.multiselect-dropdown-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.1s ease;
}

.multiselect-dropdown-option:hover,
.multiselect-dropdown-option--focused {
  background: #f3f4f6;
}

.multiselect-dropdown-option--selected {
  background: #f5f0ff;
}

.multiselect-dropdown-option--selected:hover,
.multiselect-dropdown-option--selected.multiselect-dropdown-option--focused {
  background: #ede5ff;
}

.multiselect-dropdown-select-all {
  border-bottom: 1px solid #e5e7eb;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  margin-bottom: 4px;
}

.multiselect-dropdown-option--removed {
  opacity: 0.55;
}

.multiselect-dropdown-option--removed .multiselect-dropdown-option-label {
  text-decoration: line-through;
}

.multiselect-dropdown-checkbox {
  width: 20px;
  height: 20px;
  border: 2px solid #d1d5db;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s ease;
  background: #fff;
}

.multiselect-dropdown-checkbox--checked {
  background: var(--color-primary, #6a16f0);
  border-color: var(--color-primary, #6a16f0);
  color: #fff;
}

.multiselect-dropdown-option--removed .multiselect-dropdown-checkbox--checked {
  background: #9ca3af;
  border-color: #9ca3af;
}

.multiselect-dropdown-option-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.multiselect-dropdown-option-label {
  font-size: 0.9375rem;
  font-weight: 500;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.multiselect-dropdown-option-sublabel {
  font-size: 0.8125rem;
  color: #6b7280;
}

.multiselect-dropdown-option--locked {
  opacity: 0.5;
  cursor: not-allowed;
}

.multiselect-no-results {
  padding: 16px 12px;
  text-align: center;
  font-size: 0.875rem;
  color: #9ca3af;
}

</style>
